// src/utils/droitsFnct.ts — SCE Issues
// Droits sur les FNC-T (lot F3). Une seule définition, consommée par les pages.
//  - voir l'analyse et la clôture : coordinateurs (titulaires ou suppléants),
//    AQ / CQ, administrateur et testeurs ;
//  - mener la revue et clore : revue (config/fnct.config), administrateur et testeurs ;
//  - évaluer le risque : revue et administrateur seulement (04/11) ;
//  - gérer les actions (ajouter, modifier, clore, reporter toutes) : les mêmes
//    que l'analyse ; le responsable d'une action, lui, clôt ou reporte la sienne
//    (utils/actionsFnct.peutAgirSurAction).

import { estAdmin } from '@/config/admin.config'
import { isCoordinateur } from '@/config/coordinateurs.config'
import { AQ_CQ_DEFAUT, REVUE_FNCT_DEFAUT, TESTEURS_FNCT_DEFAUT, type PersonneFnctRole } from '@/config/fnct.config'
import { rolesCharges } from '@/config/registreRoles'

const dans = (liste: readonly PersonneFnctRole[], upn: string): boolean => {
  const cible = upn.trim().toLowerCase()
  return liste.some((p) => p.upn.toLowerCase() === cible)
}

/** Les rôles FNC-T en vigueur : ceux du panneau d'administration, sinon ceux du code. */
export const testeursFnct = (): readonly PersonneFnctRole[] => rolesCharges()?.fnct.testeurs ?? TESTEURS_FNCT_DEFAUT
export const aqCqFnct = (): readonly PersonneFnctRole[] => rolesCharges()?.fnct.aqcq ?? AQ_CQ_DEFAUT
export const revueFnct = (): readonly PersonneFnctRole[] => rolesCharges()?.fnct.revue ?? REVUE_FNCT_DEFAUT

/**
 * Accès au chantier FNC-T pendant la construction : l'administrateur et les
 * testeurs (testeursFnct). Seule garde des pages /fnct, de la ligne FNC-T
 * des fiches problème et équipement, du menu du compte et du bouton +.
 */
export function accesFnct(upn: string | null | undefined): boolean {
  if (!upn) return false
  return estAdmin(upn) || dans(testeursFnct(), upn)
}

export function voitAnalyseFnct(upn: string): boolean {
  if (!upn) return false
  return accesFnct(upn) || isCoordinateur(upn) || dans(aqCqFnct(), upn)
}

export function gereActionsFnct(upn: string): boolean {
  return voitAnalyseFnct(upn)
}

export function peutCloreFnct(upn: string): boolean {
  if (!upn) return false
  return accesFnct(upn) || dans(revueFnct(), upn)
}

/**
 * Qui MÈNE la revue (Sébastien Vautelin, AQ Opérations) : la carte « Revue des
 * FNC-T » de l'accueil est pour eux seuls (03/11) — pas pour l'administrateur ni
 * les testeurs, qui peuvent clore mais ne tiennent pas la revue.
 */
export function meneLaRevueFnct(upn: string | null | undefined): boolean {
  if (!upn) return false
  return dans(revueFnct(), upn)
}

/**
 * Évaluer le risque (S, F, D, analyse AQ) : la revue et l'administrateur
 * (04/11, Guillaume). Les coordinateurs et l'AQ / CQ le voient sans le modifier.
 */
export function evalueRisqueFnct(upn: string | null | undefined): boolean {
  if (!upn) return false
  return estAdmin(upn) || meneLaRevueFnct(upn)
}

/**
 * « Actions pour la consolidation » (22-23/10) : la qualité seulement — AQ / CQ
 * (AQ Opérations comprise) — et l'administrateur, pour l'essayer.
 */
export function exporteActionsFnct(upn: string | null | undefined): boolean {
  if (!upn) return false
  return estAdmin(upn) || dans(aqCqFnct(), upn)
}
